import * as THREE from 'three';
import type { MaterialLibrary } from '../MaterialLibrary';

export type WorldArt = {
  root: THREE.Group;
  paths: THREE.Vector3[][];
  objectivePosition: THREE.Vector3;
  spawnPositions: THREE.Vector3[];
  validGround: THREE.Box2;
};

export class WorldFactory {
  constructor(private readonly materials: MaterialLibrary) {}

  create(): WorldArt {
    const root = new THREE.Group();
    root.name = 'world';

    const validGround = new THREE.Box2(new THREE.Vector2(-14, -10), new THREE.Vector2(14, 10));
    const objectivePosition = new THREE.Vector3(0, 0, -8);

    const paths = [
      [
        new THREE.Vector3(0, 0, 8.5),
        new THREE.Vector3(0, 0, 4.5),
        new THREE.Vector3(-3.5, 0, 2.5),
        new THREE.Vector3(-3.5, 0, -1.5),
        new THREE.Vector3(2.5, 0, -3.5),
        new THREE.Vector3(2.5, 0, -6),
        new THREE.Vector3(0, 0, -8),
      ],
      [
        new THREE.Vector3(-4, 0, 8.5),
        new THREE.Vector3(-6, 0, 6),
        new THREE.Vector3(-8, 0, 3),
        new THREE.Vector3(-7, 0, 0),
        new THREE.Vector3(-6, 0, -3),
        new THREE.Vector3(-3, 0, -6),
        new THREE.Vector3(0, 0, -8),
      ],
      [
        new THREE.Vector3(4, 0, 8.5),
        new THREE.Vector3(6, 0, 6),
        new THREE.Vector3(8, 0, 3),
        new THREE.Vector3(7, 0, 0),
        new THREE.Vector3(6, 0, -3),
        new THREE.Vector3(3, 0, -6),
        new THREE.Vector3(0, 0, -8),
      ],
    ];

    root.add(this.createSea());
    root.add(this.createBeach());
    root.add(this.createDunes());
    for (const pathPoints of paths) {
      root.add(this.createPath(pathPoints));
    }
    root.add(this.createObjectiveBunker(objectivePosition));
    root.add(this.createDecorations());

    return {
      root,
      paths,
      objectivePosition,
      spawnPositions: paths.map((p) => p[0].clone()),
      validGround,
    };
  }

  private createSea(): THREE.Mesh {
    const sea = new THREE.Mesh(new THREE.PlaneGeometry(60, 18), this.materials.water);
    sea.rotation.x = -Math.PI / 2;
    sea.position.set(0, -0.05, 16);
    sea.receiveShadow = true;
    return sea;
  }

  private createBeach(): THREE.Mesh {
    const beach = new THREE.Mesh(new THREE.PlaneGeometry(32, 24), this.materials.sand);
    beach.rotation.x = -Math.PI / 2;
    beach.position.y = -0.02;
    beach.receiveShadow = true;
    return beach;
  }

  private createDunes(): THREE.Group {
    const dunes = new THREE.Group();

    const dunePositions = [
      { x: -11, z: -4, sx: 4, sz: 10 },
      { x: 11, z: -2, sx: 4.5, sz: 12 },
      { x: -9, z: 7, sx: 5, sz: 6 },
      { x: 9, z: 8, sx: 4, sz: 5 },
      { x: -6, z: -9, sx: 5, sz: 3.5 },
      { x: 6, z: -9, sx: 5, sz: 3.5 },
    ];

    for (const d of dunePositions) {
      const dune = new THREE.Mesh(new THREE.SphereGeometry(1, 16, 10, 0, Math.PI * 2, 0, Math.PI / 2.4), this.materials.grass);
      dune.position.set(d.x, -0.2, d.z);
      dune.scale.set(d.sx, 0.6, d.sz);
      dune.receiveShadow = true;
      dune.castShadow = true;
      dunes.add(dune);
    }

    return dunes;
  }

  private createPath(points: THREE.Vector3[]): THREE.Group {
    const pathGroup = new THREE.Group();

    const curve = new THREE.CatmullRomCurve3(points, false, 'catmullrom', 0.35);
    const totalLength = curve.getLength();
    const spacing = 0.55;
    const count = Math.max(2, Math.floor(totalLength / spacing));
    const geometry = new THREE.CircleGeometry(0.14, 10);
    const tracks = new THREE.InstancedMesh(geometry, this.materials.track, count);
    tracks.receiveShadow = true;
    tracks.position.y = 0.01;

    const dummy = new THREE.Object3D();
    const pos = new THREE.Vector3();
    const last = new THREE.Vector3();

    for (let i = 0; i < count; i++) {
      const t = i / (count - 1);
      curve.getPointAt(t, pos);

      if (i > 0 && pos.distanceToSquared(last) < 0.08) {
        dummy.scale.set(0, 0, 0);
      } else {
        const tangent = curve.getTangentAt(t, new THREE.Vector3());
        const angle = Math.atan2(tangent.x, tangent.z) + (Math.random() - 0.5) * 0.4;
        dummy.position.set(pos.x + (Math.random() - 0.5) * 0.12, 0, pos.z + (Math.random() - 0.5) * 0.12);
        dummy.rotation.x = -Math.PI / 2;
        dummy.rotation.z = angle;
        dummy.scale.set(0.5 + Math.random() * 0.4, 0.8 + Math.random() * 0.5, 1);
      }

      dummy.updateMatrix();
      tracks.setMatrixAt(i, dummy.matrix);
      last.copy(pos);
    }

    pathGroup.add(tracks);
    return pathGroup;
  }

  private createObjectiveBunker(position: THREE.Vector3): THREE.Group {
    const bunker = new THREE.Group();
    bunker.position.copy(position);

    const base = new THREE.Mesh(new THREE.BoxGeometry(2.4, 1.2, 2.2), this.materials.darkConcrete);
    base.position.y = 0.6;
    base.castShadow = true;
    base.receiveShadow = true;
    bunker.add(base);

    const roof = new THREE.Mesh(new THREE.CylinderGeometry(1.5, 1.5, 2.6, 3, 1, false, 0, Math.PI), this.materials.concrete);
    roof.rotation.z = Math.PI / 2;
    roof.position.y = 1.35;
    roof.castShadow = true;
    bunker.add(roof);

    const firingSlot = new THREE.Mesh(new THREE.BoxGeometry(1.2, 0.18, 0.15), this.materials.darkSteel);
    firingSlot.position.set(0, 0.75, 1.12);
    bunker.add(firingSlot);

    const flagPole = new THREE.Mesh(new THREE.CylinderGeometry(0.04, 0.04, 2.2, 6), this.materials.rust);
    flagPole.position.set(0.9, 1.4, -0.7);
    flagPole.castShadow = true;
    bunker.add(flagPole);

    const flag = new THREE.Mesh(new THREE.BoxGeometry(0.7, 0.45, 0.04), new THREE.MeshStandardMaterial({ color: '#8c4b32', roughness: 0.8 }));
    flag.position.set(1.15, 2.1, -0.7);
    flag.castShadow = true;
    bunker.add(flag);

    return bunker;
  }

  private createDecorations(): THREE.Group {
    const decor = new THREE.Group();

    const barbedWirePositions = [
      new THREE.Vector3(-2.2, 0, -4.2),
      new THREE.Vector3(-2.2, 0, -5),
      new THREE.Vector3(2.2, 0, 1.2),
      new THREE.Vector3(2.2, 0, 2),
      new THREE.Vector3(-5, 0, 0.5),
      new THREE.Vector3(4.8, 0, -6.5),
    ];

    for (const pos of barbedWirePositions) {
      const post1 = new THREE.Mesh(new THREE.CylinderGeometry(0.04, 0.04, 0.7, 5), this.materials.darkSteel);
      post1.position.set(pos.x - 0.35, 0.35, pos.z);
      post1.castShadow = true;
      decor.add(post1);

      const post2 = new THREE.Mesh(new THREE.CylinderGeometry(0.04, 0.04, 0.7, 5), this.materials.darkSteel);
      post2.position.set(pos.x + 0.35, 0.35, pos.z);
      post2.castShadow = true;
      decor.add(post2);

      const wire = new THREE.Mesh(new THREE.CylinderGeometry(0.03, 0.03, 0.7, 5), this.materials.steel);
      wire.rotation.z = Math.PI / 2;
      wire.position.set(pos.x, 0.5, pos.z);
      decor.add(wire);

      const wire2 = new THREE.Mesh(new THREE.CylinderGeometry(0.03, 0.03, 0.7, 5), this.materials.steel);
      wire2.rotation.z = Math.PI / 2;
      wire2.position.set(pos.x, 0.62, pos.z);
      decor.add(wire2);
    }

    const cratePositions = [
      new THREE.Vector3(-7, 0, -6),
      new THREE.Vector3(-7.8, 0, -5.6),
      new THREE.Vector3(7.2, 0, -7),
      new THREE.Vector3(7.8, 0, -6.5),
    ];

    for (const pos of cratePositions) {
      const crate = new THREE.Mesh(new THREE.BoxGeometry(0.7, 0.7, 0.7), this.materials.olive);
      crate.position.set(pos.x, 0.35, pos.z);
      crate.rotation.y = Math.random() * 0.6;
      crate.castShadow = true;
      crate.receiveShadow = true;
      decor.add(crate);
    }

    const tankTrapPositions = [
      new THREE.Vector3(3.8, 0, -2.2),
      new THREE.Vector3(-4.2, 0, 4.2),
      new THREE.Vector3(5, 0, 4.5),
    ];

    for (const pos of tankTrapPositions) {
      const trap = new THREE.Mesh(new THREE.TetrahedronGeometry(0.45, 0), this.materials.darkSteel);
      trap.position.set(pos.x, 0.25, pos.z);
      trap.rotation.set(Math.random(), Math.random(), Math.random());
      trap.castShadow = true;
      decor.add(trap);
    }

    return decor;
  }
}
